import { useState, FormEvent } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import AuthSingleCardLayout from '@/components/auth/AuthSingleCardLayout';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { FiLock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid or missing reset token. Please request a new link.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthSingleCardLayout
        badge="Invalid link"
        title="Reset link expired"
        subtitle="This recovery link is missing or no longer valid. Request a new one to continue."
      >
        <div className="space-y-4 text-center">
          <FiAlertCircle className="mx-auto h-12 w-12 text-red-600" />
          <Link
            to="/forgot-password"
            className="inline-block rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Request a new link
          </Link>
        </div>
      </AuthSingleCardLayout>
    );
  }

  return (
    <AuthSingleCardLayout
      badge="New password"
      title="Create a new password"
      subtitle="Set a new password below and return directly to your workspace."
    >
          {success ? (
            <div className="text-center space-y-4">
              <FiCheckCircle className="mx-auto w-12 h-12 text-done" />
              <h3 className="text-lg font-semibold text-slate-950">Password updated</h3>
              <p className="text-sm text-slate-600">
                Your password has been reset. Redirecting to sign in...
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="flex items-start rounded-2xl border border-red-200 bg-red-50 p-4">
                  <FiAlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Input
                id="password"
                name="password"
                type="password"
                label="New password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<FiLock className="w-5 h-5" />}
                fullWidth
                required
              />

              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                label="Confirm new password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                leftIcon={<FiLock className="w-5 h-5" />}
                fullWidth
                required
              />

              <Button type="submit" variant="primary" size="lg" fullWidth isLoading={loading}>
                Reset password
              </Button>
            </form>
          )}
    </AuthSingleCardLayout>
  );
}
